import { Alert, Button, Drawer, Table, Typography } from "@arco-design/web-react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { APPROVAL_UPDATE_PERMISSION } from "../config";
import { useApprovalSettingsRuntime } from "../runtime/useApprovalSettingsRuntime";
import { formatBranchSummary, formatFlowSummary } from "../service/approvalSettingsService";
import type { ApprovalTypeSettings } from "../types";
import { ApprovalFlowEditor } from "./ApprovalFlowEditor";
import "./approval.css";

/** 审批设置页：每种审批类型一行流程摘要（主流程和各分支），能改设置的人在抽屉里编辑。 */
export function ApprovalSettingsFeaturePage() {
  const user = useCurrentAuthUser().data;
  const canUpdate = canUsePermission(user, APPROVAL_UPDATE_PERMISSION);
  const runtime = useApprovalSettingsRuntime(canUpdate);
  return (
    <main className="approval-page">
      <div>
        <h1 className="approval-page-title">审批设置</h1>
        <p className="approval-page-description">配置每种审批逐级由谁审批。改设置只影响之后提交的单子。</p>
      </div>
      {runtime.queryError && <Alert type="error" content={runtime.queryError} action={<Button onClick={runtime.refresh}>重试</Button>} />}
      <Table<ApprovalTypeSettings>
        rowKey="typeCode"
        loading={runtime.loading}
        data={runtime.items}
        pagination={false}
        columns={[
          { title: "审批类型", dataIndex: "typeLabel", width: 160 },
          {
            title: "审批流程",
            render: (_, row) => (
              <div className="approval-flow-summary">
                {row.mainFlow
                  ? <span>{formatFlowSummary(row.mainFlow)}</span>
                  : <Typography.Text type="warning">{row.branches.length ? "主流程未设置，没命中分支的单子提交会失败" : "未设置，提交会失败"}</Typography.Text>}
                {row.branches.map((branch) => <span key={branch.branchNo}>{formatBranchSummary(branch)}</span>)}
              </div>
            ),
          },
          {
            title: "最后修改",
            width: 240,
            render: (_, row) => (row.updatedByName && row.updatedAt ? `${row.updatedByName} · ${row.updatedAt.replace("T", " ")}` : "—"),
          },
          ...(canUpdate
            ? [{
                title: "操作",
                width: 100,
                render: (_: unknown, row: ApprovalTypeSettings) => (
                  <Button type="text" aria-label={`编辑${row.typeLabel}流程`} onClick={() => runtime.openEditor(row)}>编辑</Button>
                ),
              }]
            : []),
        ]}
      />
      <Drawer
        title={runtime.draft ? `${runtime.draft.typeLabel} · 审批流程` : ""}
        visible={runtime.draft !== null}
        width={720}
        okText="保存"
        cancelText="取消"
        confirmLoading={runtime.saving}
        onOk={() => { void runtime.save(); }}
        onCancel={runtime.closeEditor}
        maskClosable={!runtime.saving}
        unmountOnExit
      >
        {runtime.draft && <ApprovalFlowEditor runtime={runtime} />}
      </Drawer>
    </main>
  );
}
